import { NODE_TITLES, NOTIFY_CHANNEL_NAMES, type FlowEdge, type FlowGraph, type FlowNode, type FlowNodeState } from '@1qa/shared';
import type { FlowNames } from './types';

const RULE_TEXT = {
  all_passed: 'All passed?',
  any_failed: 'Anything failed?',
  pass_rate_at_least: 'Pass rate at least',
  test_failed: 'Did this test fail?',
} as const;

/** Title and one-line detail shown on a step in the diagram. */
export function describeNode(node: FlowNode, names: FlowNames): { title: string; detail: string } {
  const title = node.label || NODE_TITLES[node.type];
  switch (node.type) {
    case 'start':
      return { title, detail: 'The flow begins here' };
    case 'end':
      return { title, detail: 'The path ends here' };
    case 'test':
      return { title, detail: node.data.testCaseId ? (names.tests[node.data.testCaseId] ?? 'Missing test') : 'Choose a test' };
    case 'group':
      return { title, detail: node.data.groupId ? (names.groups[node.data.groupId] ?? 'Missing group') : 'Choose a group' };
    case 'join':
      return { title, detail: node.data.wait === 'any' ? 'Continue after the first branch' : 'Wait for all branches' };
    case 'notify': {
      const d = node.data;
      if (!d.channels.length) return { title, detail: 'Choose where to send it' };
      const when = d.when === 'failed' ? ' · if something failed' : d.when === 'passed' ? ' · if all passed' : '';
      return { title, detail: `${d.channels.map((c) => NOTIFY_CHANNEL_NAMES[c]).join(', ')}${when}` };
    }
    case 'condition': {
      const d = node.data;
      const scope = d.scope === 'flow' ? 'whole flow so far' : 'previous step';
      if (d.rule === 'pass_rate_at_least') return { title, detail: `${RULE_TEXT[d.rule]} ${d.threshold ?? '…'}% (${scope})` };
      if (d.rule === 'test_failed') return { title, detail: `${RULE_TEXT[d.rule]} ${d.testCaseId ? (names.tests[d.testCaseId] ?? 'Missing test') : '…'}` };
      return { title, detail: `${RULE_TEXT[d.rule]} (${scope})` };
    }
  }
}

/** Data carried by a step on the canvas. */
export interface StepData extends Record<string, unknown> {
  node: FlowNode;
  title: string;
  detail: string;
  /** Editor: the step has a problem that stops the flow from running. */
  problem?: boolean;
  /** Run view: the step's progress. */
  run?: FlowNodeState;
}

/** A Notify step's result in words: it sent, chose not to send (its "Send when"), or could not deliver. */
export function notifyLabel(run: StepData['run']): string | undefined {
  if (run?.status === 'ERROR') return 'Not delivered';
  if (run?.status === 'PASSED') return run.message?.startsWith('Not sent') ? 'Not sent' : 'Sent';
  return undefined;
}

export interface CanvasNode {
  id: string;
  type: 'step';
  position: { x: number; y: number };
  data: StepData;
  deletable?: boolean;
}

export interface CanvasEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  label?: string;
  data?: { branch?: 'yes' | 'no' };
}

export function toCanvasNodes(graph: FlowGraph, names: FlowNames, extra: (n: FlowNode) => Partial<StepData> = () => ({})): CanvasNode[] {
  return graph.nodes.map((n) => ({
    id: n.id,
    type: 'step',
    position: n.position,
    // Start cannot be removed (a flow always has one).
    deletable: n.type !== 'start',
    data: { node: n, ...describeNode(n, names), ...extra(n) },
  }));
}

export function toCanvasEdges(graph: FlowGraph): CanvasEdge[] {
  return graph.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.branch ?? null,
    ...(e.branch ? { label: e.branch === 'yes' ? 'Yes' : 'No', data: { branch: e.branch } } : {}),
  }));
}

/** Back from the canvas to the saved diagram (positions rounded). */
export function fromCanvas(nodes: CanvasNode[], edges: CanvasEdge[]): FlowGraph {
  return {
    nodes: nodes.map((n) => ({ ...n.data.node, position: { x: Math.round(n.position.x), y: Math.round(n.position.y) } })),
    edges: edges.map((e): FlowEdge => {
      const branch = e.data?.branch ?? (e.sourceHandle === 'yes' || e.sourceHandle === 'no' ? e.sourceHandle : undefined);
      return { id: e.id, source: e.source, target: e.target, ...(branch ? { branch } : {}) };
    }),
  };
}

/** A short unique id for a new step or connection. */
export function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

/** JSON with object keys sorted, so equal diagrams compare equal whatever order their keys came in. */
export function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))) : v,
  );
}
